<script setup>
import { computed, ref, shallowRef, toValue, useTemplateRef, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { uniq } from 'lodash-es'
import { v4 as uuid4 } from 'uuid'
import { computedWithControl } from '@vueuse/core'
import { transformer } from '@provise/lib/transformer'
import { useApi, useApiResource } from '@provise/composables/api'
import { useConfigsStore } from '@/stores/configs'
import { useMessageStack } from '@provise/stores/messagestack'
import Select from '@/components/select/select.vue'
import SelectID from '@/components/select/selectIdObject.vue'
import Editor from '@/components/editor/editor.vue'
import Uploads from '@/views/complaints/item/uploads.vue'
import FormCtrl from '@provise/components/form.vue'
import { useUserStore } from '@provise/stores/user.js'
import { useUiStore } from '@provise/stores/ui.js'

const CHANNEL_POSTAL = computed(() => channels.value.find(c => c.key === 'postal_send').id)
const CHANNEL_EMAIL = computed(() => channels.value.find(c => c.key === 'email').id)
const CHANNEL_INTERNAL = computed(() => channels.value.find(c => c.key === 'internal').id)

const { blockScreen } = storeToRefs(useUiStore())
const { push: notify } = useMessageStack()
const emit = defineEmits(['save', 'close', 'sent'])
const props = defineProps({
  url: String,
  complaintId: [Number, null],
  reply: [null, Object],
})
const model = defineModel()
const userStore = useUserStore()
const { user } = storeToRefs(userStore)

const cs = useConfigsStore()
const { config } = storeToRefs(cs)

const docTemplates = shallowRef([])
const emailTemplates = shallowRef([])
const departments = shallowRef([])
const complaint = shallowRef({})
const recipientsManual = shallowRef([])
const repeatMessId = uuid4()
const isEditing = shallowRef(false)

const byEmail = computed(() => model.value.channel.key == 'email')
const complaintUrl = computed(() => `complaints/${props.complaintId}`)
const communicationUrl = computed(() => `complaints/${props.complaintId}/communication`)
const uploadUrl = computed(() =>
  model.value.id ? `complaints/${props.complaintId}/communication/${model.value.id}/upload` : null
)
const sendUrl = computed(() =>
  model.value.id ? `complaints/${props.complaintId}/communication/${model.value.id}/send` : null
)
/**
 * all emails: department, from complaint, added by-hand
 */
const recipients = computedWithControl([model, complaint, recipientsManual, departments], () => {
  let result = [...recipientsManual.value]

  const cv = complaint.value
  const dpts = (departments.value || []).map(d => d.email)
  const mv = model.value

  if (cv.email) {
    result.push(cv.email)
  }
  if (mv.email_addresses) {
    result = result.concat(mv.email_addresses)
  }
  result = [...result, ...dpts]

  result = uniq(result).map(email => ({ email }))
  return result || []
})
const channels = computedWithControl(config, () => cs.get('communication.channel', []).value)
const docTemplate = shallowRef(null)
const emailTemplate = shallowRef(null)
const formEditable = ref(true)

const tr = transformer(
  [
    ['email_addresses', 'arr', email => ({ email })],
    ['doc_template', 'id'],
    ['email_template', 'id'],
    'doc_text',
    'email_text',
    ['channel', 'id'],
    'repeat_mess',
    'address',
  ],
  false,
  false
)

const isSubmitDisabled = shallowRef(true)

useApi({ url: 'doc_templates', model: docTemplates, immediate: true, pick: d => d.data || [] })
useApi({ url: 'email_templates', model: emailTemplates, immediate: true, pick: d => d.data || [] })
useApi({ url: 'departments', model: departments, immediate: true, pick: d => d.data || [] })
useApi({ url: complaintUrl, model: complaint, immediate: true })
const { refresh } = useApi({
  url: communicationUrl,
  watch: [communicationUrl],
  before: () => Boolean(communicationUrl.value),
  model,
  transform,
  pick: d => {
    d.email_addresses = (d.email_addresses || []).map(({ email }) => email) || []
    return d
  },
})
const {
  refresh: doSend,
  pending: doSendPending,
  abort: doSendAbort,
} = useApi({
  url: sendUrl,
  before: () => Boolean(sendUrl.value),
})
const apiResource = useApiResource(communicationUrl, model, refresh)
const blankShow = computed(() => {
  if (props.reply || model.value?.official_reply?.id || model.value?.sender_email) {
    return false
  }
  return [CHANNEL_EMAIL, CHANNEL_POSTAL].map(toValue).includes(model.value?.channel?.id)
})
const officialReplyShow = computed(() => {
  return !props.reply && model.value?.official_reply?.id
})
const clientReply = computed(() => {
  return model.value?.sender_email
})
const emailShow = computed(
  () =>
    [CHANNEL_EMAIL].map(toValue).includes(model.value?.channel?.id) && !model.value?.sender_email
)
const isInternalMessage = computed(() =>
  [CHANNEL_INTERNAL].map(toValue).includes(model.value?.channel?.id)
)
const blockEditCommunication = computed(() => {
  return model.value?.id && isInternalMessage && model.value?.author?.id !== user?.value.id
})

function transform(d) {
  const dat = tr(d)
  if (props.reply) {
    dat.official_reply = { id: props.reply.id }
  }
  if (d.uploads) {
    dat.uploads = d.uploads.map(u => ({ id: u.id }))
  }
  return dat
}

const f = useTemplateRef('form-ctrl')
const blankField = useTemplateRef('blank-field')
const emailField = useTemplateRef('email-field')

watch(
  model,
  mv => {
    isSubmitDisabled.value = !mv.channel?.id
  },
  { immediate: true, deep: true }
)

watch(
  () => model.value.id,
  async (id, oldid) => {
    if (f.value && oldid) {
      await f.value.reset()
    }
    if (id) {
      await apiResource('get')
    }
  },
  { immediate: true }
)

// set doc text when template changed
watch(
  docTemplate,
  dtv => {
    const current = emailTemplates.value.find(dt => dt.id == dtv?.id)
    if (!current) {
      return
    }
    model.value = { ...model.value, doc_text: current.text }
  },
  { flush: 'post' }
)
// set email text when template changed
watch(
  emailTemplate,
  etv => {
    const current = emailTemplates.value.find(et => et.id == etv?.id)
    if (!current) {
      return
    }
    model.value = { ...model.value, email_text: current.text }
  },
  { flush: 'post' }
)

watch(
  doSendPending,
  p => {
    formEditable.value = !p
  },
  { immediate: true }
)

function onAddEmailAddress(addr) {
  if (recipients.value.map(({ email }) => email).includes(addr)) {
    return
  }
  recipientsManual.value = [...recipientsManual.value, addr]
}

async function save() {
  if (blankShow.value && model.value.doc_text && !model.value.doc_template) {
    notify('Выберите бланк')
    blankField.value.scrollIntoView({ behavior: 'smooth' })
    return
  }
  await apiResource()
  blockScreen.value = false
  await f.value.reset()
  emit('save', model.value)
}

async function onSend() {
  if (
    [CHANNEL_EMAIL].map(toValue).includes(model.value?.channel?.id) &&
    model.value.email_addresses.length == 0
  ) {
    notify('Укажите email адрес')
    emailField.value.scrollIntoView({ behavior: 'smooth' })
    return
  }
  await doSend('get')
  notify('Сообщение успешно отправлено')
  await apiResource('get')
  emit('save', model.value)
}

function chanlShouldSel(m) {
  let mv = m
  if (!mv) {
    return
  }
  return Number(mv.id)
}
async function onAddUploads(cb) {
  cb()
}
async function onAffirmSend() {
  await useApi({
    url: `complaints/${props.complaintId}/communication/${model.value.id}/send`,
    immediate: true,
    method: 'get',
  })
  await apiResource('get')
  emit('sent')
}
</script>
<template>
  <FormCtrl
    entity-name="communication"
    :can-edit="formEditable"
    :entity="model"
    :edit-permission="!blockEditCommunication"
    :can-save="!isSubmitDisabled"
    :is-visible="!model.sent"
    @form:editable="isEditing = $event"
    @form:apply="save"
    @form:reset="apiResource('get')"
    ref="form-ctrl"
  >
    <div class="grid">
      <div class="g-col-6" v-if="!clientReply">
        <label class="form-label">Выберите канал коммуникации</label>
        <SelectID
          v-model="model.channel"
          :options="channels"
          values="value"
          :disabled="model.sent"
          :should-select="chanlShouldSel"
        />
      </div>
      <div class="g-col-6" v-else>
        <label class="form-label">Ответ клиента</label>
      </div>
      <template v-if="blankShow">
        <div class="g-col-6" ref="blank-field">
          <label class="form-label">Укажите бланк документа</label>
          <SelectID v-model="model.doc_template" :options="docTemplates" :disabled="model.sent" />
        </div>
        <b class="g-col-12">Введите текст, который будет напечатан на бланке</b>
        <div class="grid g-col-12">
          <label class="g-col-3 form-label d-flex mb-0 align-items-center">
            Использовать шаблон
          </label>
          <SelectID
            class="g-col-9"
            v-model="docTemplate"
            :options="emailTemplates"
            :disabled="model.sent"
          />
        </div>
        <div class="g-col-12">
          <Editor v-model="model.doc_text" height="20rem" :disabled="model.sent" />
        </div>
      </template>
      <template v-if="officialReplyShow">
        <div class="g-col-6">
          <span class="form-label">Официальный ответ</span>
          <p style="margin-top: 13px">
            <a
              v-if="model.official_reply.uploads.length"
              :download="model.official_reply.uploads[0].filename"
              :href="`/api/v1.0/download/${model.official_reply.uploads[0].secure_filename}`"
              >{{ model.official_reply.name }} ({{ model.official_reply.uploads[0].filename }})</a
            >
          </p>
        </div>
      </template>
      <div class="g-col-12" v-if="emailShow" ref="email-field">
        <label class="form-label">Укажите получателей</label>
        <Select
          v-model="model.email_addresses"
          :options="recipients"
          :multiple="true"
          keys="email"
          values="email"
          :should-select="v => v"
          :add-options="true"
          @add-option="onAddEmailAddress"
          :disabled="model.sent"
        />
      </div>
      <div class="g-col-12" v-if="!emailShow && !isInternalMessage" ref="email-field">
        <label class="form-label">Получатель</label>
        <input type="text" class="form-control" v-model="model.address" />
      </div>

      <b class="g-col-12" v-if="emailShow">Введите сопроводительный текст в электронной почте</b>
      <b class="g-col-12" v-else-if="!clientReply">Ваше сообщение</b>

      <div class="grid g-col-12" v-if="emailShow">
        <label class="g-col-3 form-label d-flex mb-0 align-items-center">Использовать шаблон</label>
        <SelectID
          v-model="emailTemplate"
          :options="emailTemplates"
          class="g-col-9"
          :disabled="model.sent"
        />
      </div>
      <Editor v-model="model.email_text" class="g-col-12" height="20rem" :disabled="model.sent" />

      <div class="g-col-12">
        <Uploads
          v-model="model"
          division="complaint.communication"
          v-if="model.id"
          :url="uploadUrl"
          :disabled="model.sent"
          @add-files="onAddUploads"
        />
        <div v-else class="alert alert-light">
          <i class="bi bi-file-earmark" />
          Чтобы приложить файлы, сохраните коммуникацию
        </div>
      </div>

      <div class="form-check form-switch g-col-12" v-if="!isInternalMessage">
        <input
          class="form-check-input"
          type="checkbox"
          :id="repeatMessId"
          v-model="model.repeat_mess"
          :disabled="!formEditable"
        />
        <label class="form-check-label" :for="repeatMessId"> Это обращение повторное </label>
      </div>
    </div>

    <template #post-controls v-if="!model?.sent">
      <div class="hstack gap-2" v-if="!isEditing">
        <button
          class="btn btn-outline-danger"
          type="button"
          @click="$emit('close')"
          v-if="!model.id"
        >
          <i class="bi bi-x-square" />
          Отменить
        </button>
        <button
          class="btn btn-warning"
          type="button"
          @click="onSend"
          v-if="model.id && !clientReply && emailShow"
          :disabled="model.sent || !formEditable"
        >
          <template v-if="doSendPending">
            <div class="spinner-border spinner-border-sm me-2" role="status" />
            Отправка…
          </template>
          <template v-else>
            <i class="bi bi-check-square" />
            Отправить
          </template>
        </button>
        <button type="button" class="btn btn-info" v-if="doSendPending" @click.self="doSendAbort">
          <i class="bi bi-stop-circle" />
          Остановить
        </button>
        <button
          type="button"
          class="btn btn-info"
          v-if="model.id && !byEmail"
          @click="onAffirmSend"
        >
          <i class="bi bi-send" />
          Информация отправлена
        </button>
      </div>
    </template>
  </FormCtrl>
</template>
